// ─────────────────────────────────────────────────────────────────────────────
// Story Module — TypeScript interfaces matching Swagger spec
// Covers: GetStoryListing, GetActiveCategories, GetActiveFamilyList,
//         GetFamilyMemberList
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Story visibility / ownership type used in the listing filter.
 * null = All (no filter), 1 = Own, 2 = Shared, 3 = Tagged
 */
export enum StoryType {
  Own    = 1,
  Shared = 2,
  Tagged = 3,
}

// ── Listing request ───────────────────────────────────────────────────────────

/** POST body for /Story/GetStoryListing */
export interface StoryListingRequest {
  pageNumber:       number;
  pageSize:         number;
  searchText:       string;
  /** null = All Stories */
  categoryId:       number | null;
  /** null = All types (Own + Shared + Tagged) */
  storyType:        StoryType | null;
  /** null = All family groups */
  familySpaceId:    string | null;
  /** null / empty = no creator filter */
  creatorIds:       string[] | null;
  /** null / empty = no tagged-person filter */
  taggedPersonIds:  string[] | null;
  sortColumn:       string | null;
  sortDirection:    string | null;
}

// ── Listing response ──────────────────────────────────────────────────────────

/** A single story card returned inside the listing response. */
export interface StoryItem {
  storyId:      string;
  title:        string;
  thumbnailUrl: string;
  mediaUrl:     string;
  /** 0 = Audio, 1 = Video — kept for future use */
  mediaType:    number;
  mediaTypeName: string;
  /** Duration in seconds — format with formatDuration() */
  duration:     number;
  categoryName: string;
  categoryId:   number;
  familyName:   string;
  /** ISO date string — format with getRelativeTime() */
  createdOn:    string;
  viewCount:    number;
  likeCount:    number;
  /** true → story was created in response to a scheduled prompt */
  isFromPrompt: boolean;
  /** 1 = Own, 2 = Shared, 3 = Tagged */
  storySource:  number;
  totalRecords: number;
  /** Author display name — populated by backend */
  storyAuthorName?: string;
  /** Author profile photo URL — populated by backend */
  authorPhotoURL?:  string;
  /** Vimeo video ID — present once the video has been queued to Vimeo */
  vimeoVideoId?:       string | null;
  /**
   * Vimeo processing/upload status.
   * null  → video is still being uploaded/processed (show "upload in progress")
   * any string value → upload complete, story is fully playable
   */
  vimeoUploadStatus?:  string | null;
}

/**
 * Response shape for GET /Story/GetStoryMasterData.
 * Contains all filter-dropdown data needed by the story listing page.
 */
export interface StoryMasterData {
  categories?:      StoryCategory[];
  activeCategories?: StoryCategory[];
  familySpaces?:    FamilySpace[];
  activeFamilyList?: FamilySpace[];
  familyMembers?:   FamilyMember[];
  familyMemberList?: FamilyMember[];
  creators?:        Creator[];
  allCreatorList?:  Creator[];
  creatorList?:     Creator[];
}

/** Top-level data shape for the GetStoryListing response. */
export interface StoryListingData {
  stories:      StoryItem[];
  totalRecords: number;
}

// ── Category ──────────────────────────────────────────────────────────────────

/** One item from GET /Story/GetActiveCategories */
export interface StoryCategory {
  categoryId:  number;
  categoryName: string;
  iconUrl:      string;
  totalPrompt:  number;
}

// ── Family space ──────────────────────────────────────────────────────────────

/** One item from GET /Story/GetActiveFamilyList */
export interface FamilySpace {
  familySpaceId: string;
  familyName:    string;
}

// ── Family member (for tagging / sharing) ─────────────────────────────────────

/** One item from GET /Story/GetFamilyMemberList */
export interface FamilyMember {
  familyMemberId: string;
  image:          string;
  fullName:       string;
  relationship:   string;
  familySpaceId:  string;
  familyName:     string;
}

/** One item from GET /Story/GetAllCreatorList */
export interface Creator {
  userId:   string;
  fullName: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Story Detail — GET /Story/GetStoryDetail/{storyId}
// ─────────────────────────────────────────────────────────────────────────────

/** Reaction type IDs matching POST /Common/GetAllMasterList (typeId=12). */
export enum ReactionType {
  Heart     = 1,
  Like      = 2,
  Celebrate = 3,
  Support   = 4,
}

/** One item from GET /Common/GetAllMasterList reactionTypeList. */
export interface ReactionTypeMasterItem {
  id:   number;
  name: string;
}

/** One item from POST /Common/GetAllMasterList reportReasonList (typeId=13). */
export interface ReportReasonItem {
  id:   number;
  name: string;
}

/** Response data shape from POST /Common/GetAllMasterList. */
export interface MasterListData {
  reactionTypeList: ReactionTypeMasterItem[] | null;
  reportReasonList: ReportReasonItem[]       | null;
}

/** A family member tagged inside a story. */
export interface TaggedPerson {
  familyMemberId: string;
  fullName:       string;
  profilePhoto:   string;
}

/** A person the story was shared with. */
export interface SharedWithPerson {
  userId:       string;
  fullName:     string;
  profilePhoto: string;
  sharedOn:     string;
}

/** A single reaction entry returned with the story detail. */
export interface StoryReaction {
  reactionId:    string;
  userId:        string;
  /** Display name of the user who reacted. */
  userName:      string;
  /** Matches ReactionType enum (0=Heart, 1=Smile, 2=Wow, 3=Sad). */
  reactionType:  number;
  /** true when this reaction belongs to the currently logged-in user. */
  isCurrentUser: boolean;
  createdOn:     string;
}

/** A compact story card shown in the Related Stories section. */
export interface RelatedStory {
  storyId:      string;
  title:        string;
  categoryName: string;
  thumbnailUrl: string;
  /** 0 = Audio, 1 = Video, 2 = Photo */
  mediaType:    number;
  /** Duration in seconds */
  duration:     number;
  createdOn:    string;
  viewCount:    number;
}

/**
 * Full story detail shape from GET /Story/GetStoryDetail/{storyId}.
 * Maps exactly to the Swagger response schema.
 */
export interface StoryDetail {
  storyId:                string;
  title:                  string;
  mediaUrl:               string;
  /** Vimeo playback URL (used when vimeoUploadStatus === 2 = Ready). */
  vimeoVideoUrl:          string;
  /** Vimeo video id. */
  vimeoVideoId:           string;
  /** 0 = Pending, 1 = Processing, 2 = Ready. */
  vimeoUploadStatus:      number;
  thumbnailUrl:           string;
  /** 0 = Audio, 1 = Video, 2 = Photo */
  mediaType:              number;
  mediaTypeName:          string;
  /** Duration in seconds — format with formatDuration(). */
  duration:               number;
  categoryName:           string;
  categoryId:             number;
  /** Display name of the user who uploaded the story. */
  uploadedBy:             string;
  uploadedByProfilePhoto: string;
  uploadedByUserId:       string;
  viewCount:              number;
  reactionCount:          number;
  /** ISO date string (date portion only, e.g. "2026-05-28"). */
  storyDate:              string;
  location:               string;
  emotionalTone:          string;
  transcriptVerbatim:     string;
  transcriptNarrative:    string;
  /** 0 = show Verbatim transcript, 1 = show Narrative transcript. */
  selectedTranscriptType: number;
  visibilityTypeId:       number;
  createdOn:              string;
  /** true when the story was uploaded by the currently logged-in user. */
  isOwnStory:             boolean;
  shareUrl:               string;
  taggedPeople:           TaggedPerson[]    | null;
  sharedWithPeople:       SharedWithPerson[] | null;
  reactions:              StoryReaction[];
  relatedStories:         RelatedStory[]    | null;
}

// ── Reaction requests ─────────────────────────────────────────────────────────

/** POST body for /Story/AddReaction */
export interface AddReactionRequest {
  storyId:      string;
  reactionType: number;
}

/** DELETE body for /Story/RemoveReaction */
export interface RemoveReactionRequest {
  requestId: string;
}

/**
 * PUT /Story/UpdateStoryTranscript
 * TranscriptType: 1 = Verbatim, 2 = Narrative
 */
export interface UpdateTranscriptRequest {
  storyId:        string;
  transcriptType: number;
  description:    string;
}

/** POST body for /Story/ShareStoryWithMembers */
export interface ShareStoryWithMembersRequest {
  storyId:           string;
  sharedWithUserIds: string[];
}

/** POST body for /Story/TagStoryWithMembers */
export interface TagStoryWithMembersRequest {
  storyId:         string;
  tagdWithUserIds: string[];
}

/** POST body for /Story/ReportStory */
export interface ReportStoryRequest {
  storyId:           string;
  reportReasonIds:   number[];
  additionalDetails: string;
}

export interface EmotionalTone {
  emotionalToneId: number;
  toneName: string;
}

export interface CreateAndPublishStoryRequest {
  categoryId: number;
  mediaType: number;
  questions: {
    questionId: string;
    questionSource: number;
    questionOrder: number;
    startTime: number;
    endTime: number;
  }[];
  title: string;
  mediaFileName: string;
  thumbnailFileName?: string;
  duration: number;
  storyDate: string;
  location: string;
  emotionalToneId: number | null;
  visibilityTypeId: number;
  trimStart: number;
  trimEnd: number;
  taggedFamilyMemberIds: string[];
  sharedWithUserIds: string[];
}
